import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsMobile, prefersFullView } from '../lib/useIsMobile';
import { Navigate } from 'react-router-dom';
import { Button, Callout, Card, Grid, Text } from '@tremor/react';
import { fetchOverview } from '../api/client';
import { currentUser } from '../api/auth';
import { canSeeMargin } from '../lib/roles';
import { useStoreFilter } from '../lib/storeFilter';
import { sliceOverviewByStore } from '../lib/overviewStoreSlice';
import { t } from '../lib/i18n';
import Tour, { hasTourBeenSeen, markTourSeen } from '../components/Tour';
import { TOURS } from '../lib/tours';
import type { OverviewPayload, User } from '../api/types';
import Header from '../components/Header';
import ExceptionCards from '../components/ExceptionCards';
import KpiRow from '../components/KpiRow';
import SalesPeriodChart from '../components/SalesPeriodChart';
import TopPartsTable from '../components/TopPartsTable';
import TopCustomersTable from '../components/TopCustomersTable';
import LowStockList from '../components/LowStockList';
import SlowMoversList from '../components/SlowMoversList';
import SalesByStoreChart from '../components/SalesByStoreChart';
import FreshnessFooter from '../components/FreshnessFooter';
import HourDrilldownModal from '../components/HourDrilldownModal';
import StoreDrilldownModal from '../components/StoreDrilldownModal';

export default function Overview() {
  // currentUser is async (it hits /api/me on the Go backend). Tri-state:
  //   undefined = still loading        -> render nothing (avoids login-flash)
  //   null      = no/expired session   -> redirect to /login
  //   User      = logged in            -> render OverviewInner
  const [user, setUser] = useState<User | null | undefined>(undefined);
  // Must be called here, before any early return, to satisfy React hooks order.
  const isMobile = useIsMobile();
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Phone-sized screen + owner role: send to the mobile summary, unless the
  // user has already chosen "Vista completa" for this session.
  if (isMobile && user.role === 'owner' && !prefersFullView()) {
    return <Navigate to="/movil" replace />;
  }
  return <OverviewInner user={user} />;
}

function OverviewInner({ user }: { user: User }) {
  const [data, setData] = useState<OverviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // App-wide store selection (header picker) — persists across pages.
  const [storeFilter] = useStoreFilter(user);
  const [drill, setDrill] = useState<{ hour: string; date: string } | null>(null);
  const [storeDrill, setStoreDrill] = useState<{ sid: number; storeName: string } | null>(null);
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-start the tour on first visit. Defer until after the data lands
  // so the tour's element selectors actually resolve (KpiRow renders only
  // when visibleData is set).
  useEffect(() => {
    if (!data) return;
    if (!hasTourBeenSeen(TOURS.overview.key)) {
      // Small delay so layout settles + the user gets to see the page
      // before the dialog pops up.
      const id = setTimeout(() => setTourOpen(true), 600);
      return () => clearTimeout(id);
    }
  }, [data]);

  // Listen for the global "open tour" event the help drawer fires when
  // the user clicks "Mostrar guía de esta página".
  useEffect(() => {
    const onOpen = () => setTourOpen(true);
    window.addEventListener('pacebi:open-tour', onOpen);
    return () => window.removeEventListener('pacebi:open-tour', onOpen);
  }, []);

  // Wrapped in useCallback so the Reintentar / Actualizar buttons get a
  // stable reference and don't tear off React's button-handler identity
  // checks.
  const load = useCallback(() => {
    let alive = true;
    setError(null);
    setLoading(true);
    fetchOverview(user.role)
      .then((p) => { if (alive) { setData(p); setLoading(false); } })
      .catch((e: Error) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [user.role]);

  useEffect(() => load(), [load]);

  // v2.75: the export carries one dashboard slice per store (KPI tiles,
  // hourly chart, top-10 lists). When the picked store has one, the tiles
  // and lists swap to it; AR stays chain-wide (accounts, not stores). An
  // older bi_export.p has no slices → chain-wide numbers + the callout below.
  const storeSlice = useMemo(
    () => (storeFilter === null ? undefined : data?.by_store?.find((b) => b.sid === storeFilter)),
    [data, storeFilter],
  );

  const visibleData = useMemo(
    () => (data ? sliceOverviewByStore(data, storeFilter) : null),
    [data, storeFilter],
  );

  const storeFilterActive = storeFilter !== null && data !== null && data.stores.length > 1;

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        user={user}
        storeFilter
        right={
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              variant="light"
              loading={loading && data !== null /* spinner only on manual refresh, not the first load (skeleton handles that) */}
              disabled={loading}
              onClick={load}
              aria-label={t.dashboard.refresh}
              data-tour="refresh-btn"
            >
              ↻ {t.dashboard.refresh}
            </Button>
          </div>
        }
      />
      <main className="flex-1 px-6 py-6 space-y-6 max-w-screen-2xl mx-auto w-full">
        {error && (
          <Callout
            title={t.dashboard.errorTitle}
            color="rose"
            // No icon — keep the dep surface small. Color carries the meaning.
          >
            <div className="space-y-2">
              <Text>{error}</Text>
              <Button size="xs" variant="primary" onClick={load}>
                {t.dashboard.retry}
              </Button>
            </div>
          </Callout>
        )}

        {storeFilterActive && !storeSlice && (
          <Callout title={t.dashboard.chainWideCalloutTitle} color="sky">
            {t.dashboard.chainWideCalloutBody}
          </Callout>
        )}
        {storeFilterActive && storeSlice && (
          <Text className="text-xs text-tremor-content">{t.dashboard.arChainWideNote}</Text>
        )}

        {!data && !error && <OverviewSkeleton />}

        {visibleData && (
          <>
            <div data-tour="kpi-row">
              <KpiRow kpis={visibleData.kpis} showMargin={canSeeMargin(user)} />
            </div>
            <ExceptionCards />
            <Grid numItemsLg={2} className="gap-4">
              <div data-tour="sales-period-chart">
                <SalesPeriodChart
                  today={visibleData.sales_today}
                  sid={storeFilter}
                  onSelectHour={(hour, date) => setDrill({ hour, date })}
                />
              </div>
              {visibleData.sales_by_store.length > 0 && (
                <SalesByStoreChart
                  rows={visibleData.sales_by_store}
                  onSelectStore={(sid, storeName) => setStoreDrill({ sid, storeName })}
                />
              )}
            </Grid>
            <Grid numItemsLg={2} className="gap-4">
              <TopPartsTable rows={visibleData.top_parts} />
              <TopCustomersTable rows={visibleData.top_customers} />
            </Grid>
            <LowStockList rows={visibleData.low_stock} stores={visibleData.stores} />
            <SlowMoversList rows={visibleData.slow_movers} stores={visibleData.stores} />
          </>
        )}
      </main>
      <FreshnessFooter meta={visibleData?.meta ?? null} />
      {drill && (
        <HourDrilldownModal
          hour={drill.hour}
          date={drill.date}
          onClose={() => setDrill(null)}
        />
      )}
      {storeDrill && (
        <StoreDrilldownModal
          sid={storeDrill.sid}
          storeName={storeDrill.storeName}
          onClose={() => setStoreDrill(null)}
        />
      )}
      {tourOpen && (
        <Tour
          tourKey={TOURS.overview.key}
          steps={TOURS.overview.steps}
          onClose={() => { setTourOpen(false); markTourSeen(TOURS.overview.key); }}
        />
      )}
    </div>
  );
}

// OverviewSkeleton renders structurally-equivalent placeholders so the
// page doesn't reflow when the data arrives. Four KPI placeholders, two
// chart-sized blocks, two table-sized blocks. Pure CSS — no animation
// library dep. The pulse comes from Tailwind's animate-pulse.
function OverviewSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <Grid numItemsSm={2} numItemsLg={4} className="gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <div className="animate-pulse space-y-2">
              <div className="h-3 w-24 bg-tremor-background-muted rounded" />
              <div className="h-7 w-40 bg-tremor-background-muted rounded" />
              <div className="h-3 w-32 bg-tremor-background-muted rounded" />
            </div>
          </Card>
        ))}
      </Grid>
      <Grid numItemsLg={2} className="gap-4">
        {[0, 1].map((i) => (
          <Card key={i}>
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-48 bg-tremor-background-muted rounded" />
              <div className="h-64 bg-tremor-background-muted rounded" />
            </div>
          </Card>
        ))}
      </Grid>
      <Grid numItemsLg={2} className="gap-4">
        {[0, 1].map((i) => (
          <Card key={i}>
            <div className="animate-pulse space-y-2">
              <div className="h-4 w-40 bg-tremor-background-muted rounded" />
              {[0, 1, 2, 3, 4].map((j) => (
                <div key={j} className="h-6 bg-tremor-background-muted rounded" />
              ))}
            </div>
          </Card>
        ))}
      </Grid>
      <Text className="text-tremor-content text-xs text-center">{t.dashboard.loading}</Text>
    </div>
  );
}
